import React, { useCallback } from "react";
import { connect } from "react-redux";

import Form from "antd/es/form";
import { FormInstance } from "antd/es/form/hooks/useForm";
import Radio from "antd/es/radio";
import Space from "antd/es/space";
import { useIntl } from "react-intl";

import Organization from "@mapmycustomers/shared/types/Organization";
import { isDefined } from "@mapmycustomers/shared/util/assert";

import RadioGroup from "@app/component/input/RadioGroup";
import LeaderboardVisibilitySettingValue from "@app/enum/LeaderboardVisibilitySettingValue";
import OrganizationSetting from "@app/enum/OrganizationSetting";
import LeaderboardSettings from "@app/scene/settings/type/LeaderboardSettings";
import showSaveSettingSuccessfulMessage from "@app/scene/settings/util/showSaveSettingSuccessfulMessage";
import { getOrganizationId, getOrganizationSettings } from "@app/store/iam";
import { updateOrganization } from "@app/store/iam/actions";
import { RootState } from "@app/store/rootReducer";
import useSetting from "@app/util/hook/useSetting";

import styles from "./LeadGenerationCommon.module.scss";

interface Props {
  form: FormInstance<LeaderboardSettings>;
  onUpdateOrganization: typeof updateOrganization.request;
  organizationId?: number;
  organizationSettings: Organization["settings"];
  setDirty: () => void;
  unsetDirty: () => void;
}

const Leaderboard: React.FC<Props> = ({
  form,
  onUpdateOrganization,
  organizationId,
  organizationSettings,
  setDirty,
  unsetDirty,
}) => {
  const intl = useIntl();
  const setting = useSetting(organizationSettings, OrganizationSetting.LEADERBOARD_VISIBILITY);

  const handleSubmit = useCallback(() => {
    const { leaderboardVisibility } = form.getFieldsValue();
    if (organizationId) {
      onUpdateOrganization({
        callback: () => {
          showSaveSettingSuccessfulMessage(intl);
          unsetDirty();
        },
        organization: { id: organizationId },
        settings: [
          setting && leaderboardVisibility
            ? { ...setting, value: leaderboardVisibility }
            : undefined,
        ].filter(isDefined),
      });
    }
  }, [setting, form, intl, organizationId, onUpdateOrganization, unsetDirty]);

  return (
    <Form
      form={form}
      initialValues={{
        leaderboardVisibility: setting?.value,
      }}
      onFieldsChange={setDirty}
      onFinish={handleSubmit}
    >
      <div>
        {intl.formatMessage({
          id: "settings.organizationDetails.stackRank.description",
          defaultMessage: "Show Stack Rank to",
          description: "Settings organization details - Stack Rank - description",
        })}
      </div>
      <Form.Item name={OrganizationSetting.LEADERBOARD_VISIBILITY}>
        <RadioGroup size="small">
          <Space className={styles.space} direction="vertical">
            <Radio value={LeaderboardVisibilitySettingValue.OWNER}>
              {intl.formatMessage({
                id: "settings.organizationDetails.stackRank.check.owner",
                defaultMessage: "Organization owners only",
                description:
                  "Settings organization details - Stack Rank - Organization owners only",
              })}
            </Radio>
            <Radio value={LeaderboardVisibilitySettingValue.OWNER_MANAGER}>
              {intl.formatMessage({
                id: "settings.organizationDetails.stackRank.check.owner_manager",
                defaultMessage: "Team managers and owners",
                description:
                  "Settings organization details - Stack Rank - Team managers and owners",
              })}
            </Radio>
            <Radio value={LeaderboardVisibilitySettingValue.PUBLIC}>
              {intl.formatMessage({
                id: "settings.organizationDetails.stackRank.check.public",
                defaultMessage: "All users",
                description: "Settings organization details - Stack Rank - All users",
              })}
            </Radio>
          </Space>
        </RadioGroup>
      </Form.Item>
    </Form>
  );
};

const mapStateToProps = (state: RootState) => ({
  organizationId: getOrganizationId(state),
  organizationSettings: getOrganizationSettings(state) ?? [],
});

const mapDispatchToProps = {
  onUpdateOrganization: updateOrganization.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(Leaderboard);
